import Axios from 'axios'
import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'
import type { RequestConfig, RequestInterceptors, CancelRequestSource } from './types'

export default class Service {
  // axios实例
  instance: AxiosInstance

  // 拦截器对象
  interceptorsObj?: RequestInterceptors

  /*
   * 请求
   * */
  pendingMap?: Map<any, any>

  /* 存放所有请求的 URL 集合
   * 请求之前需要将 URL push到该集合
   * 请求完毕后将 URL 从集合中删除
   * 请求响应之后删除
   * */
  requestUrlList?: string[]

  /* 存在取消方法的集合
   * 在创建请求后将取消请求方法 push 到该集合中
   * 请求完毕后需要将 URL 从集合中删除
   * 请求响应后删除
   * */
  cancelRequestSourceList?: CancelRequestSource[]

  constructor(config: RequestConfig) {
    this.pendingMap = new Map()
    this.requestUrlList = []
    this.cancelRequestSourceList = []
    this.instance = Axios.create(config)
    this.interceptorsObj = config.interceptors

    // 全局请求拦截器
    // 拦截器执行顺序：接口请求 -> 实例请求 -> 全局请求 -> 实例响应 -> 全局响应 -> 接口响应
    this.instance.interceptors.request.use(
      (requestConfig: AxiosRequestConfig) => {
        return requestConfig as InternalAxiosRequestConfig<any>
      },
      (err: any) => Promise.reject(err),
    )

    // 使用实例拦截器
    this.instance.interceptors.request.use(
      this.interceptorsObj?.requestInterceptors as any,
      this.interceptorsObj?.requestInterceptorsCatch as any,
    )
    this.instance.interceptors.response.use(
      this.interceptorsObj?.responseInterceptors as any,
      this.interceptorsObj?.requestInterceptorsCatch as any,
    )

    // 全局响应拦截器保证最后的执行
    this.instance.interceptors.response.use(
      (response: AxiosResponse) => response,
      (err: any) => Promise.reject(err),
    )
  }

  request<T>(config: RequestConfig): Promise<T> {
    let requestConfig = config
    return new Promise((resolve, reject) => {
      if (config.interceptors?.requestInterceptors) {
        requestConfig = config.interceptors.requestInterceptors(config)
      }
      const url = requestConfig?.url
      if (url) {
        this.requestUrlList?.push(url)
        requestConfig.cancelToken = new Axios.CancelToken(c => {
          this.cancelRequestSourceList?.push({ [url]: c })
        })
      }
      this.instance
        .request<any, T>(requestConfig)
        .then(res => {
          let response = res
          if (requestConfig.interceptors?.responseInterceptors) {
            response = requestConfig.interceptors.responseInterceptors<T>(response)
          }
          resolve(response)
        })
        .catch((err: any) => reject(err))
        .finally(() => url && this.delUrl(url))
    })
  }

  // 生成每个请求唯一的键
  private static createPendingKey(config: RequestConfig): string {
    const { url, method, params, data } = config
    const dataStr = typeof data === 'string' ? JSON.parse(data) : data
    return [url, method, JSON.stringify(params), JSON.stringify(dataStr)].join('&')
  }

  private getSourceIndex(url: string): number {
    return this.cancelRequestSourceList?.findIndex(
      (item: CancelRequestSource) => Object.keys(item)[0] === url,
    ) as number
  }

  private delUrl(url: string): void {
    const urlIndex = this.requestUrlList?.findIndex(item => item === url) as number
    const sourceIndex = this.getSourceIndex(url)
    if (urlIndex >= 0) this.requestUrlList?.splice(urlIndex as number, 1)
    if (sourceIndex >= 0) this.cancelRequestSourceList?.splice(sourceIndex as number, 1)
  }

  // 存储每个请求唯一值
  addPending(config: RequestConfig): void {
    const requestConfig = config
    const pendingKey = Service.createPendingKey(requestConfig)
    const cancelToken = new Axios.CancelToken(cancel => {
      if (!this.pendingMap?.has(pendingKey)) {
        this.pendingMap?.set(pendingKey, cancel)
      }
    })
    requestConfig.cancelToken = config.cancelToken || cancelToken
  }

  // 删除重复的请求
  removePending(config: RequestConfig): void {
    const pendingKey = Service.createPendingKey(config)
    if (this.pendingMap?.has(pendingKey)) {
      const cancelToken = this.pendingMap?.get(pendingKey)
      cancelToken(pendingKey)
      this.pendingMap?.delete(pendingKey)
    }
  }

  // 取消请求
  cancelRequest(url: string | string[]): void {
    if (typeof url === 'string') {
      const sourceIndex = this.getSourceIndex(url)
      if (sourceIndex >= 0) this.cancelRequestSourceList?.[sourceIndex][url]()
    }
    if (Array.isArray(url)) {
      url.forEach(u => {
        const sourceIndex = this.getSourceIndex(u)
        if (sourceIndex >= 0) this.cancelRequestSourceList?.[sourceIndex][u]()
      })
    }
  }

  // 取消全部请求
  cancelAllRequest() {
    this.cancelRequestSourceList?.forEach(source => {
      const key = Object.keys(source)[0]
      source[key]()
    })
  }
}
